import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarRange, ChevronDown, ChevronRight, Copy, Eye, Loader2, Play, Printer, Undo2 } from "lucide-react";
import { toast } from "@/lib/toast";

/**
 * Admin card on Library Stats for the one-time switch to the Year-folder
 * layout:  [Location] > [Permission] > [Name] > [YYYY] > [MM.DD.YY (- Exam)]
 *
 *   1. Google Drive — Preview → Run (→ Undo), via the yearReorg* admin job.
 *   2. NAS — copy/paste commands for scanner/reorg-years.sh on the NAS.
 *   3. "Year folders for new uploads" switch, so uploads match afterwards.
 */

const NAS_COMMANDS = [
  { label: "Preview (changes nothing)", cmd: "sudo bash /volume1/Infinity/scanner/reorg-years.sh preview" },
  { label: "Run it", cmd: "sudo bash /volume1/Infinity/scanner/reorg-years.sh apply" },
  { label: "Undo (only if needed)", cmd: "sudo bash /volume1/Infinity/scanner/reorg-years.sh undo" },
];

const escapeHtml = (t: string) =>
  t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Print just the "Needs attention" list (skipped folders + errors) via a
 *  throwaway iframe, so no popup blocker gets in the way. */
function printAttentionList(
  report: Array<{ patient: string; where: string; conflicts: string[] }>,
  errors: Array<{ patient: string; message: string }>,
) {
  const rows = report
    .filter((r) => r.conflicts.length > 0)
    .flatMap((r) =>
      r.conflicts.map(
        (c) =>
          `<tr><td class="box">&#9744;</td><td>${escapeHtml(r.patient)}</td><td>${escapeHtml(r.where)}</td><td class="mono">${escapeHtml(c)}</td></tr>`,
      ),
    );
  const errorRows = errors.map(
    (e) =>
      `<tr><td class="box">&#9744;</td><td>${escapeHtml(e.patient)}</td><td></td><td class="mono">${escapeHtml(e.message)}</td></tr>`,
  );
  const html = `<!doctype html><html><head><title>Year folders — needs attention</title><style>
    body { font: 12px -apple-system, Helvetica, Arial, sans-serif; margin: 32px; color: #111; }
    h1 { font-size: 18px; margin: 0 0 4px; } p { color: #555; margin: 0 0 16px; }
    h2 { font-size: 14px; margin: 20px 0 6px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #ddd; vertical-align: top; }
    th { background: #f3f3f3; } .mono { font-family: Menlo, Courier, monospace; font-size: 11px; }
    .box { width: 18px; font-size: 14px; }
  </style></head><body>
    <h1>Year folders: needs attention (Google Drive)</h1>
    <p>Printed ${escapeHtml(new Date().toLocaleString())}. These folders were skipped because the same name already
    exists in the year folder. They stay where they are. On Drive, move their files by hand.</p>
    ${rows.length ? `<h2>Skipped folders (${rows.length})</h2><table><tr><th></th><th>Patient</th><th>Location</th><th>Folder</th></tr>${rows.join("")}</table>` : ""}
    ${errorRows.length ? `<h2>Errors (${errorRows.length})</h2><table><tr><th></th><th>Patient</th><th></th><th>Message</th></tr>${errorRows.join("")}</table>` : ""}
  </body></html>`;
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  frame.contentWindow.focus();
  frame.contentWindow.print();
  setTimeout(() => frame.remove(), 1000);
}

const UNCHANGED_GROUPS = [
  { key: "noSubfolders", label: "have no subfolders (empty, or only loose files like a consent form)" },
  { key: "alreadyOrganized", label: "are already in year folders" },
  { key: "otherFoldersOnly", label: "only have non-date folders (e.g. iPhone Photos, Home Videos)" },
  { key: "onlySkipped", label: "only have a skipped folder (see Needs attention)" },
] as const;

/** Proof that every patient is accounted for: why each unchanged patient was
 *  left alone, with the names behind each count one click away. */
function UnchangedBreakdown(props: {
  unchanged: Record<(typeof UNCHANGED_GROUPS)[number]["key"], string[]>;
  changing: number;
  total: number;
  errors: number;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const unchangedTotal = UNCHANGED_GROUPS.reduce((n, g) => n + props.unchanged[g.key].length, 0);
  const accounted = props.changing + unchangedTotal + props.errors;
  return (
    <div className="rounded border border-border p-2 space-y-1">
      <div className="font-medium">
        {unchangedTotal.toLocaleString()} patient(s) need no changes. Here&apos;s why:
      </div>
      {UNCHANGED_GROUPS.map((g) => {
        const names = props.unchanged[g.key];
        if (names.length === 0) return null;
        const isOpen = open === g.key;
        return (
          <div key={g.key}>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-left hover:underline"
              onClick={() => setOpen(isOpen ? null : g.key)}
            >
              {isOpen ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
              <span className="font-semibold">{names.length.toLocaleString()}</span> {g.label}
            </button>
            {isOpen && (
              <div className="ml-4 mt-1 max-h-48 overflow-auto rounded bg-muted/30 p-2 font-mono text-[11px] space-y-0.5">
                {[...names].sort((a, b) => a.localeCompare(b)).map((n) => (
                  <div key={n}>{n}</div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className={accounted === props.total ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}>
        {accounted === props.total ? "✓" : "✗"} {props.changing.toLocaleString()} to change + {unchangedTotal.toLocaleString()} unchanged
        {props.errors > 0 && <> + {props.errors} error(s)</>} = {accounted.toLocaleString()} of {props.total.toLocaleString()} patients
        {accounted === props.total ? ". Everyone is accounted for." : ". These don't add up; tell Claude before running."}
      </div>
    </div>
  );
}

const btn =
  "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export default function YearFolderReorgCard() {
  const utils = trpc.useUtils();
  const startMutation = trpc.admin.yearReorgStart.useMutation();
  const setEnabledMutation = trpc.admin.setYearFoldersEnabled.useMutation();
  const statusQuery = trpc.admin.yearReorgStatus.useQuery(undefined, {
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const state = statusQuery.data;
  const running = state?.status === "running";
  const previewDone = state?.mode === "preview" && state.status === "done";

  const [confirm, setConfirm] = useState<null | "apply" | "undo" | "enable" | "disable">(null);
  const [showReport, setShowReport] = useState(false);
  const [lastSeenFinishedAt, setLastSeenFinishedAt] = useState<number | null>(state?.finishedAt ?? null);

  useEffect(() => {
    if (!state || (state.status !== "done" && state.status !== "error")) return;
    if (state.finishedAt === lastSeenFinishedAt) return;
    setLastSeenFinishedAt(state.finishedAt);
    if (state.status === "error") toast.error(`Drive ${state.mode} failed: ${state.error ?? "unknown error"}`);
    else if (state.mode === "preview") toast.success("Drive preview ready — review it below.");
    else toast.success(`Drive ${state.mode === "apply" ? "reorganization" : "undo"} finished — ${state.changesMade.toLocaleString()} change(s).`);
  }, [state, lastSeenFinishedAt]);

  const start = async (mode: "preview" | "apply" | "undo") => {
    try {
      const res = await startMutation.mutateAsync({ mode });
      if (!res.started) toast.info("A Drive reorg job is already running.");
      void statusQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start: ${err?.message ?? "unknown error"}`);
    }
  };

  const setEnabled = async (enabled: boolean) => {
    try {
      await setEnabledMutation.mutateAsync({ enabled });
      toast.success(enabled ? "New uploads will now use year folders." : "Year folders for new uploads turned off.");
      void utils.admin.yearReorgStatus.invalidate();
      void utils.gdrive.yearFoldersEnabled.invalidate();
    } catch (err: any) {
      toast.error(`Couldn't update the setting: ${err?.message ?? "unknown error"}`);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste it into the NAS Terminal window.");
    } catch {
      toast.error("Couldn't copy — select the text and copy it by hand.");
    }
  };

  const onConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (c === "apply" || c === "undo") void start(c);
    else if (c === "enable") void setEnabled(true);
    else if (c === "disable") void setEnabled(false);
  };

  const pct =
    state && state.patientsTotal > 0 ? Math.round((state.patientsScanned / state.patientsTotal) * 100) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarRange className="w-4 h-4" />
          Year folders
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          One-time switch to{" "}
          <span className="font-mono">Location › Permission › Name › YYYY › MM.DD.YY</span>. Moves each
          patient&apos;s date folders into a year folder and renames old <span className="font-mono">*YYYY</span>{" "}
          folders to <span className="font-mono">YYYY</span>. Nothing is re-uploaded or deleted.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* ── 1. Google Drive ─────────────────────────────────────── */}
        <div className="p-3 rounded border border-border space-y-2">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-sm font-medium">1. Google Drive</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Preview first; it changes nothing. Run unlocks once a preview finishes. Every change is logged,
                so Undo can put it all back.
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" className={btn} disabled={running || startMutation.isPending} onClick={() => void start("preview")}>
                {running && state?.mode === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                Preview
              </button>
              <button
                type="button"
                className={`${btn} border-primary/40 text-primary`}
                disabled={running || !previewDone || (state?.patientsToChange ?? 0) === 0}
                onClick={() => setConfirm("apply")}
              >
                {running && state?.mode === "apply" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Run on Drive
              </button>
              <button type="button" className={btn} disabled={running || (state?.journalSize ?? 0) === 0} onClick={() => setConfirm("undo")}>
                <Undo2 className="w-3.5 h-3.5" />
                Undo
              </button>
            </div>
          </div>

          {running && (
            <div className="text-xs text-muted-foreground">
              {state?.mode === "undo" ? "Undoing" : state?.mode === "apply" ? "Reorganizing" : "Previewing"}…{" "}
              {state?.patientsScanned.toLocaleString()} / {state?.patientsTotal.toLocaleString()} ({pct}%)
              <div className="mt-1 h-1.5 rounded bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {state && state.status !== "idle" && state.mode !== "undo" && (
            <div className="text-xs space-y-1">
              <div>
                <span className="font-medium">{state.patientsToChange.toLocaleString()}</span> of{" "}
                {state.patientsTotal.toLocaleString()} patients {state.mode === "apply" ? "changed" : "need changes"}:{" "}
                {state.foldersToMove.toLocaleString()} date folder(s) to move,{" "}
                {state.yearFoldersToCreate.toLocaleString()} year folder(s) to create,{" "}
                {state.starFoldersToRename.toLocaleString()} <span className="font-mono">*YYYY</span> to rename.
                {state.mode === "apply" && <> {state.changesMade.toLocaleString()} Drive change(s) made.</>}
              </div>
              {/* Needs attention — skipped folders + errors, pulled out of the
                  full report so they don't have to be hunted for. */}
              {(state.conflicts > 0 || state.errors.length > 0) && (
                <div className="rounded border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-2 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 font-semibold text-amber-800 dark:text-amber-300">
                      Needs attention ({state.conflicts + state.errors.length})
                    </div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border border-amber-300 dark:border-amber-800 bg-background hover:bg-muted"
                      onClick={() => printAttentionList(state.report, state.errors)}
                    >
                      <Printer className="w-3 h-3" />
                      Print list
                    </button>
                  </div>
                  {state.conflicts > 0 && (
                    <div className="text-amber-800 dark:text-amber-300">
                      Skipped because the same name already exists in the year folder. These stay where they are;
                      sort them out by hand on Drive.
                    </div>
                  )}
                  <div className="max-h-48 overflow-auto font-mono text-[11px] space-y-1">
                    {state.report
                      .filter((r) => r.conflicts.length > 0)
                      .map((r, i) => (
                        <div key={i}>
                          <div className="font-semibold">
                            {r.patient} <span className="font-normal text-muted-foreground">({r.where})</span>
                          </div>
                          {r.conflicts.map((c, j) => (
                            <div key={j} className="pl-3 text-amber-800 dark:text-amber-300">⚠ skipped {c}</div>
                          ))}
                        </div>
                      ))}
                    {state.errors.map((e, i) => (
                      <div key={`e${i}`} className="text-red-700 dark:text-red-400">
                        ✗ {e.patient}: {e.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {state.status === "done" && state.mode === "preview" && (
                <UnchangedBreakdown
                  unchanged={state.unchanged}
                  changing={state.patientsToChange}
                  total={state.patientsTotal}
                  errors={state.errors.length}
                />
              )}
              {state.report.length > 0 && (
                <button type="button" className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => setShowReport((v) => !v)}>
                  {showReport ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {showReport ? "Hide" : "Show"} per-patient report ({state.report.length.toLocaleString()})
                </button>
              )}
              {showReport && (
                <div className="max-h-80 overflow-auto rounded border border-border bg-muted/30 p-2 font-mono text-[11px] space-y-1.5">
                  {state.report.map((r, i) => (
                    <div key={i}>
                      <div className="font-semibold">
                        {r.patient} <span className="font-normal text-muted-foreground">({r.where})</span>
                      </div>
                      {r.changes.map((c, j) => (
                        <div key={j} className="pl-3">• {c}</div>
                      ))}
                      {r.conflicts.map((c, j) => (
                        <div key={j} className="pl-3 text-amber-700 dark:text-amber-400">⚠ skipped {c}</div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {state?.mode === "undo" && state.status === "done" && (
            <div className="text-xs">
              Undo reversed {state.changesMade.toLocaleString()} change(s)
              {state.errors.length > 0 && `; ${state.errors.length} couldn't be reversed (press Undo again to retry)`}.
            </div>
          )}
        </div>

        {/* ── 2. NAS ──────────────────────────────────────────────── */}
        <div className="p-3 rounded border border-border space-y-2">
          <div className="text-sm font-medium">2. NAS</div>
          <div className="text-xs text-muted-foreground">
            Runs right on the NAS. Log in over SSH as you do for the scanner redeploy, then paste one command at a
            time. <strong>Preview</strong> changes nothing. <strong>Run it</strong> pauses the scanner, moves the
            folders, updates the app&apos;s saved NAS paths so nothing re-uploads, then restarts the scanner.
          </div>
          {NAS_COMMANDS.map((c) => (
            <div key={c.cmd} className="flex items-center gap-2">
              <span className="w-40 shrink-0 text-xs text-muted-foreground">{c.label}</span>
              <code className="flex-1 min-w-0 truncate text-[11px] px-2 py-1 rounded bg-muted font-mono">{c.cmd}</code>
              <button type="button" className={btn} onClick={() => void copy(c.cmd)}>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </button>
            </div>
          ))}
        </div>

        {/* ── 3. New uploads switch ───────────────────────────────── */}
        <div className="flex items-start gap-3 p-3 rounded border border-border">
          <div className="flex-1">
            <div className="text-sm font-medium">
              3. Year folders for new uploads:{" "}
              <span className={state?.yearFoldersEnabled ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>
                {state?.yearFoldersEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              When on, patient uploads create or reuse the year folder automatically, on Drive and on local/NAS
              saves. Turn it on after steps 1 and 2. Provider A-Roll already works this way.
            </div>
          </div>
          <button
            type="button"
            className={btn}
            disabled={setEnabledMutation.isPending || !state}
            onClick={() => setConfirm(state?.yearFoldersEnabled ? "disable" : "enable")}
          >
            {state?.yearFoldersEnabled ? "Turn off" : "Turn on"}
          </button>
        </div>
      </CardContent>

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "apply" && "Reorganize Google Drive into year folders?"}
              {confirm === "undo" && "Undo the Drive reorganization?"}
              {confirm === "enable" && "Turn on year folders for new uploads?"}
              {confirm === "disable" && "Turn off year folders for new uploads?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "apply" &&
                `Moves ${state?.foldersToMove.toLocaleString() ?? 0} date folder(s) for ${state?.patientsToChange.toLocaleString() ?? 0} patient(s), as listed in the preview. Links and playback keep working, and Undo can reverse it.`}
              {confirm === "undo" &&
                `Reverses ${state?.journalSize.toLocaleString() ?? 0} logged Drive change(s): date folders go back to the patient folder, *YYYY names are restored, and new year folders go to the trash if they're still empty.`}
              {confirm === "enable" && "New patient uploads will go into Name › YYYY › MM.DD.YY. Do this after the Drive and NAS steps."}
              {confirm === "disable" && "New patient uploads will go back to Name › MM.DD.YY."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
